/** The breakdown as an ARIA treeview: modules → topics → objectives. Roving tabindex, arrow
 * keys move, Right/Left expand and collapse, Enter selects, A approves the focused node.
 * Draft nodes carry a dashed violet left border (the one meaning of violet, see DraftChip). */
import { useMemo, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

import type { ModuleNode } from "../api/types";

export type NodeKind = "module" | "topic" | "lo";
export type NodeRef = { kind: NodeKind; id: string };

type Row = { ref: NodeRef; depth: number; label: string; code?: string; status: string; hasChildren: boolean; parent?: NodeRef };

export function BreakdownTree({
  modules,
  selected,
  onSelect,
  onApprove,
}: {
  modules: ModuleNode[];
  selected: NodeRef | null;
  onSelect: (ref: NodeRef) => void;
  onApprove: (ref: NodeRef) => void;
}) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [focused, setFocused] = useState<string | null>(null);

  const rows = useMemo(() => {
    const out: Row[] = [];
    for (const m of modules) {
      const mref = { kind: "module" as const, id: m.module_id };
      out.push({ ref: mref, depth: 0, label: m.title, status: m.review_status, hasChildren: m.topics.length > 0 });
      if (collapsed.has(m.module_id)) continue;
      for (const tp of m.topics) {
        const tref = { kind: "topic" as const, id: tp.topic_id };
        out.push({ ref: tref, depth: 1, label: tp.title, status: tp.review_status, hasChildren: tp.los.length > 0, parent: mref });
        if (collapsed.has(tp.topic_id)) continue;
        for (const lo of tp.los) {
          out.push({ ref: { kind: "lo", id: lo.lo_id }, depth: 2, label: lo.text, code: lo.code, status: lo.review_status, hasChildren: false, parent: tref });
        }
      }
    }
    return out;
  }, [modules, collapsed]);

  const focusId = focused ?? selected?.id ?? rows[0]?.ref.id ?? null;

  function toggle(id: string) {
    setCollapsed((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  function onKey(e: KeyboardEvent<HTMLLIElement>, i: number) {
    const row = rows[i];
    if (!row) return;
    const go = (j: number) => {
      const target = rows[Math.max(0, Math.min(rows.length - 1, j))];
      if (!target) return;
      setFocused(target.ref.id);
      document.getElementById(`tree-${target.ref.id}`)?.focus();
    };
    switch (e.key) {
      case "ArrowDown": e.preventDefault(); go(i + 1); break;
      case "ArrowUp": e.preventDefault(); go(i - 1); break;
      case "Home": e.preventDefault(); go(0); break;
      case "End": e.preventDefault(); go(rows.length - 1); break;
      case "ArrowRight":
        e.preventDefault();
        if (row.hasChildren && collapsed.has(row.ref.id)) toggle(row.ref.id);
        else if (row.hasChildren) go(i + 1);
        break;
      case "ArrowLeft":
        e.preventDefault();
        if (row.hasChildren && !collapsed.has(row.ref.id)) toggle(row.ref.id);
        else if (row.parent) go(rows.findIndex((r) => r.ref.id === row.parent?.id));
        break;
      case "Enter": case " ": e.preventDefault(); onSelect(row.ref); break;
      case "a": case "A": case "ф": case "Ф": e.preventDefault(); onApprove(row.ref); break;
      default: return;
    }
  }

  return (
    <ul role="tree" aria-label={t("plan.tree")} className="flex flex-col gap-0.5 text-sm">
      {rows.map((row, i) => {
        const isSel = selected?.id === row.ref.id;
        const draft = row.status === "draft";
        return (
          <li
            key={row.ref.id}
            id={`tree-${row.ref.id}`}
            role="treeitem"
            aria-level={row.depth + 1}
            aria-selected={isSel}
            aria-expanded={row.hasChildren ? !collapsed.has(row.ref.id) : undefined}
            tabIndex={focusId === row.ref.id ? 0 : -1}
            onFocus={() => setFocused(row.ref.id)}
            onKeyDown={(e) => onKey(e, i)}
            onClick={() => onSelect(row.ref)}
            className={[
              "flex cursor-pointer items-center gap-1 rounded-[var(--radius-control)] py-1 pr-2 outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]",
              isSel ? "bg-[color:var(--surface-2)] font-medium" : "hover:bg-[color:var(--surface)]",
              draft ? "border-l-2 border-dashed" : "border-l-2 border-transparent",
            ].join(" ")}
            style={{ paddingLeft: `${row.depth * 16 + 6}px`, borderLeftColor: draft ? "var(--draft)" : undefined }}
          >
            {row.hasChildren ? (
              <button
                type="button"
                tabIndex={-1}
                aria-hidden
                className="w-4 text-[color:var(--ink-2)]"
                onClick={(e) => { e.stopPropagation(); toggle(row.ref.id); }}
              >
                {collapsed.has(row.ref.id) ? "▸" : "▾"}
              </button>
            ) : (
              <span className="w-4" aria-hidden />
            )}
            {row.code && <span className="font-mono text-xs text-[color:var(--ink-2)]">{row.code}</span>}
            <span className="truncate">{row.label}</span>
            <span className="ml-auto text-xs" aria-label={draft ? t("draft.draft") : t("plan.approved")} style={{ color: draft ? "var(--draft)" : "var(--ok)" }}>
              {draft ? "◌" : "✓"}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
